import { getUTCDateString, toLocalDateTimeOffsetString, compareDatesToDayPrecision } from 'helpers/dateHelper';
import React, { useCallback, useEffect } from 'react';
import { setChartBounds, saveSeriesSelection, updateZoomLevel } from './helpers';

// Helper function to calculate Y-axis range from visible data points
const calculateYRange = (series, xMin, xMax) => {
    let yMin = Infinity;
    let yMax = -Infinity;
    let pointsFound = 0;

    series.forEach(s => {
        if (s.visible && s.data && s.data.length > 0) {
            s.data.forEach(point => {
                const pointX = point.x === undefined ? point[0] : point.x;
                const pointY = point.y === undefined ? point[1] : point.y;

                if (pointX >= xMin && pointX <= xMax && pointY !== null && pointY !== undefined && !Number.isNaN(pointY)) {
                    yMin = Math.min(yMin, pointY);
                    yMax = Math.max(yMax, pointY);
                    pointsFound++;
                }
            });
        }
    });

    return { yMin, yMax, pointsFound };
};

// Helper function to apply Y-axis auto-scaling
const applyYAxisAutoScale = (chart, xMin, xMax, activeXAxisIndex = 0) => {
    const xAxis = chart.xAxis[activeXAxisIndex];
    const yAxis = chart.yAxis[0];

    const { yMin, yMax, pointsFound } = calculateYRange(chart.series, xMin, xMax);

    xAxis.setExtremes(xMin, xMax, false);

    if (chart.xAxis.length > 1) {
        const otherAxisIndex = activeXAxisIndex === 0 ? 1 : 0;
        const conversionFactor = activeXAxisIndex === 0 ? 60 : 1 / 60; // HZ to RPM or vice versa
        chart.xAxis[otherAxisIndex].setExtremes(xMin * conversionFactor, xMax * conversionFactor, false);
    }

    if (yMin !== Infinity && yMax !== -Infinity && pointsFound > 0) {
        const yRange = yMax - yMin;
        const yPadding = yRange > 0 ? yRange * 0.05 : Math.abs(yMin) * 0.1 || 1;
        yAxis.setExtremes(yMin - yPadding, yMax + yPadding, false);
    } else {
        yAxis.setExtremes(null, null, false);
    }

    chart.redraw();
};

const useCustomZoom = (props, mainChartInst, popOutChartInst, showChartPopout, zoomOut, setZooming) => {
    const { zoomInServer, resetServerZoom, isZoomed, setIsZoomed, hasKpi } = props;

    useEffect(() => {
        setZooming(false);
    }, [popOutChartInst]);

    useEffect(() => {
        if (!isZoomed) {
            mainChartInst?.container && hasKpi && zoomOut(mainChartInst);
            popOutChartInst?.container && hasKpi && zoomOut(popOutChartInst);
        }
    }, [isZoomed, mainChartInst, popOutChartInst]);

    const handleServerZoom = useCallback((event) => {
        if (event.resetSelection) {
            return;
        }
        const xAxis = event.xAxis[0];
        const minDate = new Date(xAxis.min);
        minDate.setDate(minDate.getDate());
        const formatZoomDate = props.useLocalTimeZoom ? toLocalDateTimeOffsetString : getUTCDateString;
        let maxDate = new Date(xAxis.max);
        const reachedLatestData = xAxis.axis?.dataMax === undefined || xAxis.max >= xAxis.axis.dataMax;
        if (props.useLocalTimeZoom && reachedLatestData && compareDatesToDayPrecision(maxDate, new Date())) {
            maxDate = new Date();
        }
        zoomInServer(formatZoomDate(minDate), formatZoomDate(maxDate));
        setIsZoomed(true);
    }, [zoomInServer, props.useLocalTimeZoom, setIsZoomed]);

    const selectionCallbackFn = useCallback(function (event) {
        if (zoomInServer && resetServerZoom) { // perform server zooming
            setChartBounds(this.xAxis); // reset scale (extremes) of the axes in order to scale automatically
            setChartBounds(this.yAxis);
            handleServerZoom(event);
            return false; // prevent default zoom in case of server zoom
        }

        const needsAutoScale = props.isVibrationFFT2D || props.isNotPreserveSignals;

        if (event.resetSelection) {
            if (needsAutoScale) {
                setChartBounds(this.xAxis);
                setChartBounds(this.yAxis);
                this.redraw();
                setIsZoomed(false);
                return false;
            }
            return;
        }

        if (needsAutoScale && event.yAxis?.[0]) {
            this.yAxis[0].setExtremes(event.yAxis[0].min, event.yAxis[0].max, false);
            this.redraw();
            setIsZoomed(true);
            return false; // Prevent default zoom
        }

        if (needsAutoScale && event.xAxis?.[0]) {
            const activeXAxisIndex = event.xAxis[0].axis?.index ?? 0;
            applyYAxisAutoScale(this, event.xAxis[0].min, event.xAxis[0].max, activeXAxisIndex);
            setIsZoomed(true);
            return false; // Prevent default zoom
        }

        setIsZoomed(true);
    }, [zoomInServer, resetServerZoom, props.isVibrationFFT2D, props.isNotPreserveSignals, handleServerZoom]);

    useEffect(() => {
        if (!props.options.enableNoDataZoom) {
            if (props.options.series?.length === 0) {
                if (!resetServerZoom) {
                    setIsZoomed(false);
                }
            }
        }
    }, [props.options.series?.length, mainChartInst]);

    // Only sync series/zoom from main chart to popout on initial open
    const prevShowChartPopoutRef = React.useRef(false);
    useEffect(() => {
        if (showChartPopout && prevShowChartPopoutRef.current && popOutChartInst) {
            saveSeriesSelection(popOutChartInst, mainChartInst);
            if (isZoomed) {
                updateZoomLevel(popOutChartInst, mainChartInst);
            }
        }
        prevShowChartPopoutRef.current = showChartPopout;
    }, [showChartPopout, popOutChartInst]);

    return [selectionCallbackFn];
};

export default useCustomZoom;
